import { useLocation, useNavigate } from "@entry/navigation";
import dayjs from "dayjs";
import { useReducer } from "react";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { BeneficiaryType, Flow, Routes } from "@ctypes";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { EntryBody, EntryGlassOption } from "@entry/ui";
import { useGoalFlow, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  AddBeneficiaryTypeRouteProps,
  AddBeneficiaryTypeSelectionAction,
  AddBeneficiaryTypeSelectionState,
  INVEST_TOGETHER,
} from "./types";

const initialState: AddBeneficiaryTypeSelectionState = {
  [BeneficiaryType.Child]: false,
  [BeneficiaryType.Self]: false,
  [INVEST_TOGETHER]: false,
};

const reducer = (
  state: AddBeneficiaryTypeSelectionState,
  action: AddBeneficiaryTypeSelectionAction,
) => {
  return {
    ...initialState,
    [action.type]: !state[action.type],
  };
};

export const AddBeneficiaryTypeRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectionState, dispatch] = useReducer(reducer, initialState);
  const { CHILD, SELF } = selectionState;
  const TOGETHER = selectionState[INVEST_TOGETHER];

  const flow = (location.state as AddBeneficiaryTypeRouteProps)?.flow ?? Flow.Onboarding;
  const isOnboarding = flow === Flow.Onboarding;

  const { user, beneficiaries } = useUser();
  const { resetGoalProps } = useGoalFlow();
  const { setIntake } = useEntryIntakeStore();

  const disabled = [CHILD, SELF, TOGETHER].every((value) => !value);

  const hasSelfInvestingAccount = beneficiaries?.some((b) => b?.type === BeneficiaryType.Self);

  const handleSubmit = () => {
    if (SELF || TOGETHER) {
      setIntake({ isInvestTogether: TOGETHER });

      resetGoalProps({
        beneficiaryType: BeneficiaryType.Self,
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        dateOfBirth: user?.dateOfBirth ? dayjs(user.dateOfBirth).toDate() : undefined,
        flow,
      });

      navigate(Routes.GoalType);
    } else {
      setIntake({ isInvestTogether: false });

      resetGoalProps({
        beneficiaryType: BeneficiaryType.Child,
        firstName: undefined,
        lastName: undefined,
        dateOfBirth: undefined,
        flow,
      });

      navigate(Routes.AddChild);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.EntryBeneficiaryTitle),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.AddBeneficiaryTypeRoutePrimaryCTA),
        onClick: handleSubmit,
        disabled,
      },
    },
  };

  const selectChildOption = () => {
    dispatch({ type: BeneficiaryType.Child });
  };

  const selectTogetherOption = () => {
    dispatch({ type: INVEST_TOGETHER });
  };

  const selectSelfOption = () => {
    dispatch({ type: BeneficiaryType.Self });
  };

  return (
    <OnboardingLayout {...props}>
      <EntryGlassOption
        title={i18n.t(Translations.EntryBeneficiaryChildLabel)}
        highlightText={i18n.t(Translations.EntryBeneficiaryChildHighlight)}
        subtitle={i18n.t(Translations.EntryBeneficiaryChildSubtitle)}
        selected={CHILD}
        onSelect={selectChildOption}
        hideIndicator
        rightElement={
          <img src="/images/entry/child-selection.png" alt="" style={{ height: 100 }} />
        }
      />
      <Spacing size={SpacingSizes.S} />
      <EntryBody $align="center" style={{ fontSize: 12, color: "rgba(244, 241, 242, 0.8)" }}>
        {i18n.t(Translations.EntryBeneficiaryChildHint)}
      </EntryBody>
      <Spacing size={SpacingSizes.S} />

      {isOnboarding && (
        <>
          <EntryGlassOption
            title={i18n.t(Translations.EntryBeneficiaryTogetherLabel)}
            highlightText={i18n.t(Translations.EntryBeneficiaryTogetherHighlight)}
            selected={TOGETHER}
            onSelect={selectTogetherOption}
            hideIndicator
            rightElement={
              <img
                src="/images/entry/together-selection.png"
                alt=""
                style={{ height: 120, marginRight: 8 }}
              />
            }
          />
          <Spacing size={SpacingSizes.S} />
        </>
      )}

      {!hasSelfInvestingAccount && (
        <EntryGlassOption
          title={i18n.t(Translations.EntryBeneficiaryMyselfLabel)}
          highlightText={i18n.t(Translations.EntryBeneficiaryMyselfHighlight)}
          selected={SELF}
          onSelect={selectSelfOption}
          hideIndicator
          rightElement={
            <img
              src="/images/entry/adult-selection.png"
              alt=""
              style={{ height: 100, marginRight: 8 }}
            />
          }
        />
      )}
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
